import React, { useState, useRef, useEffect, useCallback } from 'react';
import AWS from 'aws-sdk';
import ExcelJS from 'exceljs';
import { API, graphqlOperation } from "aws-amplify";
import { useFormikContext } from "formik";
import {
  Button as LumosButton,
  ChoiceInput,
  Form as LumosForm,
  Modal,
  ModalControls,
 } from "@loanpal/lumos__core";
import { Box, Text, Spinner } from "theme-ui";
import {
  getUserByEmail,
  getOffersByChannel,
  getOffersForSharedApp,
  getTokenWithRetry,
} from "./api";
import { fixSubmittingUserAbsoluteUrl } from "./admin-error-email-templates";
import { updateRequests } from "../graphql/mutations";

const REPORT_HEADERS = [
  "Partner Name",
  "Client ID",
  "Partner Primary Contact Email",
  "SF Account Owner Email",
  "Status",
  "Connected Org",
  "Submitting User Email",
  "SU exist",
  "Channel Name",
  "Has Offers",
  "Fix",
];

const REPORT_ROW_ISSUE_BG = "#fef2f2";

const NO_OFFERS_FIX =
  "Fix GoodLink channel, no offers found";
const API_AUTH_FIX =
  "API authentication failed (401). Check partner API keys";

/**
 * Build the usability-report Fix column from SU / offers check results.
 * Priority: API failure → no offers → missing submitting user (fix link).
 */
const buildUsabilityReportFix = ({
  suExist,
  hasOffers,
  requestId,
  sharedAppId,
}) => {
  if (hasOffers === "error" || suExist === "error") {
    return API_AUTH_FIX;
  }
  if (hasOffers === "No offers") {
    return NO_OFFERS_FIX;
  }
  if (suExist === "false") {
    const href = fixSubmittingUserAbsoluteUrl(requestId, sharedAppId);
    if (href) return href;
    return "Missing Requests record — cannot build fix-submitting-user link";
  }
  return "";
};

const isReportRowIssue = (row) => {
  const su = row["SU exist"];
  const offers = row["Has Offers"];
  return (
    su === "false" ||
    su === "error" ||
    offers === "No offers" ||
    offers === "error"
  );
};

const countReportRowIssues = (rows) =>
  rows.filter((row) => isReportRowIssue(row)).length;

const formatReportCompleteMessage = (rows) => {
  const total = rows.length;
  const errors = countReportRowIssues(rows);
  const pct =
    total > 0 ? ((errors / total) * 100).toFixed(1) : "0.0";
  return `Done — ${total} partners exported, ${errors} with errors (${pct}% of total). Combined file downloaded (2 sheets).`;
};

const renderReportProgress = (message) => {
  const checkingMatch = message.match(
    /^Checking partner (\d+) of (\d+): (.*)$/
  );
  if (checkingMatch) {
    const [, current, total, clientId] = checkingMatch;
    return (
      <>
        Checking partner{" "}
        <Box as="span" sx={{ fontWeight: 700 }}>
          {current}
        </Box>{" "}
        of{" "}
        <Box as="span" sx={{ fontWeight: 700 }}>
          {total}
        </Box>
        : {clientId}
      </>
    );
  }
  return message;
};

const LOCALHOST_CREDENTIALS_KEY = "goodlink-export-aws-credentials";
const REPORT_CHECKPOINT_KEY = "quicklink-usability-report-checkpoint";
const CHECKPOINT_INTERVAL = 500;

const REQUEST_IMPORT_SAMPLE_CSV = `accountName,clientId,id,salesforceId,SF Account Owner,SF Customer Success Manager,Partner Primary Contact
Green Valley Cooling & Heating,GreenValleyCoolingAndHeating,bb8308a5-1e02-4f4d-9574-6db1ddf72159,0015G00002jtPlGQAU,rachel.davis@goodleap.com,lbarlow@goodleap.com,joanna@greenvalleycooling.com
"AIR CONDITION SALES AND SERVICE, INC DBA AC SALES & SERVICE",ACSalesAndService,9ce60451-fc5a-46c1-a7bd-c69c0837baeb,001UJ00000IcJX8YAN,hlopez@goodleap.com,lbarlow@goodleap.com,jlege@ac-sales.com
"Call KS, LLC",CallKSLLC,1bc42fa8-2e43-4f8f-bb64-b2187f2aab78,001UJ00000C06gGYAR,hlopez@goodleap.com,lbarlow@goodleap.com,j.douglas@callks.com`;

const REQUEST_IMPORT_FIELDS = [
  {
    csvColumn: "SF Account Owner",
    dynamoField: "accountOwnerEmail",
    label: "SF Account Owner → accountOwnerEmail",
  },
  {
    csvColumn: "SF Customer Success Manager",
    dynamoField: "contactEmail",
    label: "SF Customer Success Manager → contactEmail",
  },
  {
    csvColumn: "Partner Primary Contact",
    dynamoField: "partnerEmail",
    label: "Partner Primary Contact → partnerEmail",
  },
];

const REQUEST_IMPORT_FORM_INITIAL = {
  fieldsToUpdate: ["accountOwnerEmail", "contactEmail", "partnerEmail"],
};

const selectedImportFieldsFromForm = (fieldsToUpdate = []) =>
  REQUEST_IMPORT_FIELDS.filter((field) =>
    fieldsToUpdate.includes(field.dynamoField)
  );

/** Only non-empty email values are written; blanks leave existing DB values unchanged. */
const buildImportRowUpdates = (row, selectedImportFields) => {
  const updates = {};
  selectedImportFields.forEach((field) => {
    if (!Object.prototype.hasOwnProperty.call(row, field.csvColumn)) return;
    const value = String(row[field.csvColumn] ?? "").trim();
    if (!value) return;
    updates[field.dynamoField] = value;
  });
  return updates;
};

const REQUEST_IMPORT_SAMPLE_HEADERS = [
  "accountName",
  "clientId",
  "id",
  "salesforceId",
  "SF Account Owner",
  "SF Customer Success Manager",
  "Partner Primary Contact",
];

const IMPORT_PREVIEW_SAMPLE_SIZE = 5;

const SIMPLE_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const inspectRequestImportCsv = (text, fieldsToUpdate = []) => {
  const selectedImportFields = selectedImportFieldsFromForm(fieldsToUpdate);
  const fatalProblems = [];
  const warnings = [];

  if (selectedImportFields.length === 0) {
    return {
      canProcess: false,
      fatalProblems: [{ message: "Select at least one field to update." }],
      warnings: [],
      headers: [],
      rowCount: 0,
      sampleRows: [],
      processableCount: 0,
      skippedCount: 0,
    };
  }

  const { headers, rows } = parseCsvText(text);

  if (headers.length === 0) {
    fatalProblems.push({ message: "CSV has no header row." });
  }

  if (headers.length > 0 && !headers.includes("id")) {
    fatalProblems.push({ message: 'CSV must include an "id" column to match Requests records.' });
  }

  const missingColumns = selectedImportFields
    .map((field) => field.csvColumn)
    .filter((column) => !headers.includes(column));
  if (missingColumns.length > 0) {
    fatalProblems.push({
      message: `CSV is missing required column(s): ${missingColumns.join(", ")}`,
    });
  }

  if (rows.length === 0) {
    fatalProblems.push({ message: "CSV has no data rows." });
  }

  const optionalHeaders = ["accountName", "clientId", "salesforceId"];
  optionalHeaders.forEach((header) => {
    if (headers.length > 0 && !headers.includes(header)) {
      warnings.push({ message: `Optional column "${header}" is not present.` });
    }
  });

  const unexpectedHeaders = headers.filter(
    (header) => header && !REQUEST_IMPORT_SAMPLE_HEADERS.includes(header)
  );
  if (unexpectedHeaders.length > 0) {
    warnings.push({
      message: `Unexpected column(s): ${unexpectedHeaders.join(", ")}`,
    });
  }

  const rowIssues = [];
  const duplicateIds = new Map();
  let processableCount = 0;
  let skippedCount = 0;

  rows.forEach((row, index) => {
    const rowNumber = index + 2;
    const recordId = row.id?.trim();
    if (!recordId) {
      skippedCount += 1;
      rowIssues.push({ rowNumber, id: "", message: "Missing id — row will be skipped." });
      return;
    }

    if (duplicateIds.has(recordId)) {
      rowIssues.push({
        rowNumber,
        id: recordId,
        message: `Duplicate id (also on row ${duplicateIds.get(recordId)}) — may overwrite the same record twice.`,
      });
    } else {
      duplicateIds.set(recordId, rowNumber);
    }

    const updates = buildImportRowUpdates(row, selectedImportFields);

    if (Object.keys(updates).length === 0) {
      skippedCount += 1;
      rowIssues.push({
        rowNumber,
        id: recordId,
        message: "All selected email fields empty — row will be skipped.",
      });
      return;
    }

    selectedImportFields.forEach((field) => {
      if (!Object.prototype.hasOwnProperty.call(row, field.csvColumn)) return;
      const value = String(row[field.csvColumn] ?? "").trim();
      if (!value) {
        rowIssues.push({
          rowNumber,
          id: recordId,
          message: `Empty ${field.csvColumn} — ${field.dynamoField} will be left unchanged.`,
        });
      }
    });

    Object.entries(updates).forEach(([field, value]) => {
      if (!SIMPLE_EMAIL_RE.test(value)) {
        rowIssues.push({
          rowNumber,
          id: recordId,
          message: `Invalid email for ${field}: "${value}"`,
        });
      }
    });

    processableCount += 1;
  });

  const sampleRows = rows.slice(0, IMPORT_PREVIEW_SAMPLE_SIZE).map((row, index) => ({
    rowNumber: index + 2,
    id: row.id?.trim() ?? "",
    accountName: row.accountName ?? "",
    clientId: row.clientId ?? "",
    updates: buildImportRowUpdates(row, selectedImportFields),
  }));

  return {
    canProcess: fatalProblems.length === 0,
    fatalProblems,
    warnings: [...warnings, ...rowIssues],
    headers,
    rowCount: rows.length,
    sampleRows,
    processableCount,
    skippedCount,
    selectedImportFields,
  };
};

const importTableSx = {
  width: "100%",
  tableLayout: "fixed",
  borderCollapse: "separate",
  borderSpacing: 0,
  fontSize: 1,
  "th, td": {
    borderBottom: "1px solid",
    borderColor: "neutral.l3",
    px: 2,
    py: 2,
    textAlign: "left",
    verticalAlign: "top",
    whiteSpace: "normal",
    wordBreak: "break-word",
    overflowWrap: "anywhere",
  },
  thead: {
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  th: {
    backgroundColor: "#f9fafb",
    fontWeight: 600,
  },
};

const RequestImportPreviewWatcher = ({ csvText, fileRevision, onPreviewChange }) => {
  const { values } = useFormikContext();

  useEffect(() => {
    if (!csvText) {
      onPreviewChange(null, values.fieldsToUpdate);
      return;
    }
    onPreviewChange(
      inspectRequestImportCsv(csvText, values.fieldsToUpdate),
      values.fieldsToUpdate
    );
  }, [csvText, fileRevision, values.fieldsToUpdate, onPreviewChange]);

  return null;
};

const RequestImportPreview = ({ fileName, preview }) => {
  if (!preview) return null;

  const sampleColumns = [
    { key: "rowNumber", label: "Row" },
    { key: "id", label: "id" },
    { key: "accountName", label: "accountName" },
    ...(preview.selectedImportFields ?? []).map((field) => ({
      key: field.dynamoField,
      label: field.csvColumn,
    })),
  ];

  const issueLimit = 20;
  const warnings = preview.warnings ?? [];
  const warningsShown = warnings.slice(0, issueLimit);
  const warningsHidden = warnings.length - warningsShown.length;

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        border: "1px solid",
        borderColor: preview.canProcess ? "neutral.l3" : "#fecaca",
        borderRadius: 4,
        backgroundColor: preview.canProcess ? "#ffffff" : "#fef2f2",
      }}
    >
      <Text sx={{ fontSize: 2, fontWeight: 600, mb: 2, display: "block" }}>
        Import preview — {fileName}
      </Text>
      <Text sx={{ fontSize: 1, mb: 2, display: "block" }}>
        {preview.rowCount} data row{preview.rowCount === 1 ? "" : "s"} detected
        {preview.headers?.length
          ? ` · ${preview.headers.length} column${preview.headers.length === 1 ? "" : "s"}`
          : ""}
        {preview.canProcess
          ? ` · ${preview.processableCount} row${preview.processableCount === 1 ? "" : "s"} ready to update`
          : ""}
        {preview.skippedCount > 0
          ? ` · ${preview.skippedCount} row${preview.skippedCount === 1 ? "" : "s"} will be skipped`
          : ""}
      </Text>

      {preview.fatalProblems?.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Text sx={{ fontSize: 1, fontWeight: 600, color: "#b91c1c", mb: 1 }}>
            Cannot import — fix these issues:
          </Text>
          <Box as="ul" sx={{ m: 0, pl: 3, fontSize: 1, color: "#b91c1c" }}>
            {preview.fatalProblems.map((problem) => (
              <Box as="li" key={problem.message} sx={{ mb: 1 }}>
                {problem.message}
              </Box>
            ))}
          </Box>
        </Box>
      )}

      {warningsShown.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Text sx={{ fontSize: 1, fontWeight: 600, color: "#b45309", mb: 1 }}>
            {preview.canProcess ? "Warnings" : "Additional issues"} (
            {warnings.length})
          </Text>
          <Box
            sx={{
              maxHeight: 160,
              overflowY: "auto",
              border: "1px solid #fde68a",
              borderRadius: 4,
              backgroundColor: "#fffbeb",
              p: 2,
            }}
          >
            <Box as="ul" sx={{ m: 0, pl: 3, fontSize: 0, color: "#92400e" }}>
              {warningsShown.map((issue, index) => (
                <Box as="li" key={`${issue.rowNumber}-${issue.id}-${index}`} sx={{ mb: 1 }}>
                  {issue.rowNumber
                    ? `Row ${issue.rowNumber}${issue.id ? ` (${issue.id})` : ""}: ${issue.message}`
                    : issue.message}
                </Box>
              ))}
            </Box>
            {warningsHidden > 0 && (
              <Text sx={{ fontSize: 0, color: "#92400e", mt: 1 }}>
                …and {warningsHidden} more
              </Text>
            )}
          </Box>
        </Box>
      )}

      {preview.sampleRows?.length > 0 && (
        <Box>
          <Text sx={{ fontSize: 1, fontWeight: 600, mb: 2 }}>
            Sample records to update (first {preview.sampleRows.length} of{" "}
            {preview.rowCount})
          </Text>
          <Box sx={{ overflowX: "auto", border: "1px solid", borderColor: "neutral.l3", borderRadius: 4 }}>
            <Box as="table" sx={importTableSx}>
              <Box as="thead">
                <Box as="tr">
                  {sampleColumns.map((col) => (
                    <Box as="th" key={col.key}>
                      {col.label}
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box as="tbody">
                {preview.sampleRows.map((row) => (
                  <Box as="tr" key={`${row.rowNumber}-${row.id}`}>
                    {sampleColumns.map((col) => (
                      <Box as="td" key={col.key}>
                        {col.key === "rowNumber" || col.key === "id" || col.key === "accountName"
                          ? row[col.key] ?? ""
                          : row.updates?.[col.key] ?? ""}
                      </Box>
                    ))}
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );
};

const RequestImportUploadActions = ({
  importRunning,
  importFileRef,
  onChooseFile,
  onFileSelected,
  onClear,
  hasImportPreview,
}) => {
  const { values } = useFormikContext();
  const selectedImportFields = selectedImportFieldsFromForm(values.fieldsToUpdate);

  return (
    <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
      <input
        ref={importFileRef}
        type="file"
        accept=".csv,text/csv"
        onChange={onFileSelected}
        hidden
      />
      <LumosButton
        type="button"
        onClick={onChooseFile}
        disabled={importRunning || selectedImportFields.length === 0}
      >
        Choose CSV file
      </LumosButton>
      {hasImportPreview && (
        <LumosButton type="button" onClick={onClear} disabled={importRunning}>
          Clear import preview
        </LumosButton>
      )}
      {hasImportPreview && !importRunning && (
        <Text sx={{ fontSize: 1, color: "muted" }}>
          File inspected — review preview below, then process when ready.
        </Text>
      )}
    </Box>
  );
};

const IMPORT_RESULT_STATUS = {
  UPDATED: "updated",
  SKIPPED: "skipped",
  FAILED: "failed",
  PENDING: "pending",
};

const IMPORT_RESULT_ROW_COLORS = {
  [IMPORT_RESULT_STATUS.UPDATED]: {
    backgroundColor: "#dcfce7",
    color: "#166534",
  },
  [IMPORT_RESULT_STATUS.SKIPPED]: {
    backgroundColor: "#fef9c3",
    color: "#854d0e",
  },
  [IMPORT_RESULT_STATUS.FAILED]: {
    backgroundColor: "#fee2e2",
    color: "#991b1b",
  },
  [IMPORT_RESULT_STATUS.PENDING]: {
    backgroundColor: "#f9fafb",
    color: "#374151",
  },
};

const IMPORT_RESULT_STATUS_LABELS = {
  [IMPORT_RESULT_STATUS.UPDATED]: "Updated",
  [IMPORT_RESULT_STATUS.SKIPPED]: "Skipped",
  [IMPORT_RESULT_STATUS.FAILED]: "Failed",
  [IMPORT_RESULT_STATUS.PENDING]: "Pending",
};

const formatImportError = (err) => {
  const errors = err?.errors ?? err?.error?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    return errors.map((e) => e.message).join("; ");
  }
  return err?.message ?? "Update failed";
};

const buildInitialImportResults = (rows, selectedImportFields) =>
  rows.map((row, index) => ({
    rowNumber: index + 2,
    id: row.id?.trim() ?? "",
    accountName: row.accountName ?? "",
    status: IMPORT_RESULT_STATUS.PENDING,
    message: "",
    updates: buildImportRowUpdates(row, selectedImportFields),
  }));

const RequestImportProcessButton = ({ importRunning, onProcess, canProcess }) => {
  return (
    <Box sx={{ mt: 3 }}>
      <LumosButton
        type="button"
        onClick={onProcess}
        disabled={importRunning || !canProcess}
      >
        {importRunning ? (
          <Box
            as="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Spinner size={20} />
            Processing...
          </Box>
        ) : (
          "Process import"
        )}
      </LumosButton>
    </Box>
  );
};

const RequestImportResultsTable = ({ results, selectedImportFields, importRunning }) => {
  if (!results?.length) return null;

  const updatedCount = results.filter(
    (row) => row.status === IMPORT_RESULT_STATUS.UPDATED
  ).length;
  const skippedCount = results.filter(
    (row) => row.status === IMPORT_RESULT_STATUS.SKIPPED
  ).length;
  const failedCount = results.filter(
    (row) => row.status === IMPORT_RESULT_STATUS.FAILED
  ).length;

  const resultColumns = [
    { key: "rowNumber", label: "Row" },
    { key: "status", label: "Status" },
    { key: "id", label: "id" },
    { key: "accountName", label: "accountName" },
    ...(selectedImportFields ?? []).map((field) => ({
      key: field.dynamoField,
      label: field.csvColumn,
    })),
    { key: "message", label: "Details" },
  ];

  return (
    <Box
      sx={{
        mt: 3,
        p: 3,
        border: "1px solid",
        borderColor: "neutral.l3",
        borderRadius: 4,
        backgroundColor: "#ffffff",
      }}
    >
      <Text sx={{ fontSize: 2, fontWeight: 600, mb: 2, display: "block" }}>
        Import results
        {importRunning ? " (in progress)" : ""}
      </Text>
      <Box sx={{ display: "flex", gap: 3, flexWrap: "wrap", mb: 3, fontSize: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: 2,
              backgroundColor: IMPORT_RESULT_ROW_COLORS.updated.backgroundColor,
              border: "1px solid #86efac",
            }}
          />
          <Text>{updatedCount} updated</Text>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: 2,
              backgroundColor: IMPORT_RESULT_ROW_COLORS.skipped.backgroundColor,
              border: "1px solid #fde047",
            }}
          />
          <Text>{skippedCount} skipped</Text>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Box
            sx={{
              width: 14,
              height: 14,
              borderRadius: 2,
              backgroundColor: IMPORT_RESULT_ROW_COLORS.failed.backgroundColor,
              border: "1px solid #fca5a5",
            }}
          />
          <Text>{failedCount} failed</Text>
        </Box>
      </Box>
      <Box
        sx={{
          maxHeight: 360,
          overflowY: "auto",
          border: "1px solid",
          borderColor: "neutral.l3",
          borderRadius: 4,
        }}
      >
        <Box as="table" sx={importTableSx}>
          <Box as="thead">
            <Box as="tr">
              {resultColumns.map((col) => (
                <Box as="th" key={col.key}>
                  {col.label}
                </Box>
              ))}
            </Box>
          </Box>
          <Box as="tbody">
            {results.map((row) => {
              const rowColors =
                IMPORT_RESULT_ROW_COLORS[row.status] ??
                IMPORT_RESULT_ROW_COLORS.pending;

              return (
                <Box
                  as="tr"
                  key={`${row.rowNumber}-${row.id}`}
                  sx={{
                    backgroundColor: rowColors.backgroundColor,
                    color: rowColors.color,
                  }}
                >
                  {resultColumns.map((col) => (
                    <Box as="td" key={col.key}>
                      {col.key === "status"
                        ? IMPORT_RESULT_STATUS_LABELS[row.status] ?? row.status
                        : col.key === "message"
                        ? row.message
                        : col.key === "rowNumber" ||
                          col.key === "id" ||
                          col.key === "accountName"
                        ? row[col.key] ?? ""
                        : row.updates?.[col.key] ?? ""}
                    </Box>
                  ))}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

const resolveRequestsTableName = (tableName, sharedAppTableName) => {
  if (tableName?.startsWith("Requests-")) return tableName;
  const isDev = sharedAppTableName?.includes("-dev");
  return isDev
    ? "Requests-4he23jw7efc3vo3bspj27imobi-dev"
    : "Requests-ddrj5opqljgbtnn5p4d2i7d25i-prod";
};

/** Map clientId → contact emails + request id from Requests (prefer newest updatedAt). */
const buildRequestContactByClientId = (requests = []) => {
  const byClientId = new Map();
  requests.forEach((req) => {
    const clientId = String(req?.clientId ?? "").trim();
    if (!clientId) return;
    const next = {
      requestId: String(req.id ?? "").trim(),
      partnerEmail: String(req.partnerEmail ?? "").trim(),
      accountOwnerEmail: String(req.accountOwnerEmail ?? "").trim(),
      updatedAt: req.updatedAt || req.createdAt || "",
    };
    const prev = byClientId.get(clientId);
    if (!prev || String(next.updatedAt) > String(prev.updatedAt)) {
      byClientId.set(clientId, next);
    }
  });
  return byClientId;
};

const parseCsvLine = (line) => {
  const result = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"' && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else if (ch === '"') {
        inQuotes = false;
      } else {
        current += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ",") {
      result.push(current);
      current = "";
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
};

const parseCsvText = (text) => {
  const lines = text.trim().split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length < 2) {
    return { headers: [], rows: [] };
  }
  const headers = parseCsvLine(lines[0]).map((h) => h.trim());
  const rows = lines.slice(1).map((line) => {
    const values = parseCsvLine(line);
    const row = {};
    headers.forEach((header, index) => {
      row[header] = (values[index] ?? "").trim();
    });
    return row;
  });
  return { headers, rows };
};

const downloadSampleImportCsv = () => {
  const downloadAnchor = document.createElement("a");
  downloadAnchor.href =
    "data:text/csv;charset=utf-8," + encodeURIComponent(REQUEST_IMPORT_SAMPLE_CSV);
  downloadAnchor.download = "sample-import-emails.csv";
  document.body.appendChild(downloadAnchor);
  downloadAnchor.click();
  document.body.removeChild(downloadAnchor);
};

const updateRequestFieldsViaGraphql = async (id, fields) => {
  const keys = Object.keys(fields);
  if (keys.length === 0) return;

  await API.graphql(
    graphqlOperation(updateRequests, {
      input: { id, ...fields },
    })
  );
};

const isLocalhost = () => {
  const host = window.location.hostname;
  return host === "localhost" || host === "127.0.0.1" || host === "[::1]";
};

const loadLocalhostCredentials = () => {
  if (!isLocalhost()) return null;
  try {
    const raw = sessionStorage.getItem(LOCALHOST_CREDENTIALS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.accessKeyId?.trim() && parsed?.secretAccessKey?.trim()) {
      return {
        accessKeyId: parsed.accessKeyId.trim(),
        secretAccessKey: parsed.secretAccessKey.trim(),
      };
    }
  } catch (err) {
    console.log("Could not restore localhost export credentials", err);
  }
  return null;
};

const saveLocalhostCredentials = (accessKeyId, secretAccessKey) => {
  if (!isLocalhost()) return;
  sessionStorage.setItem(
    LOCALHOST_CREDENTIALS_KEY,
    JSON.stringify({ accessKeyId, secretAccessKey })
  );
};

const clearLocalhostCredentials = () => {
  if (!isLocalhost()) return;
  sessionStorage.removeItem(LOCALHOST_CREDENTIALS_KEY);
};

const loadReportCheckpoint = (tableName) => {
  if (!tableName) return null;
  try {
    const raw = localStorage.getItem(REPORT_CHECKPOINT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (
      parsed?.sharedAppTableName !== tableName ||
      !Number.isFinite(parsed.lastProcessedIndex) ||
      !Array.isArray(parsed.rows)
    ) {
      return null;
    }
    return parsed;
  } catch (err) {
    console.log("Could not restore usability report checkpoint", err);
    return null;
  }
};

const saveReportCheckpoint = (tableName, lastProcessedIndex, rows, totalPartners) => {
  try {
    localStorage.setItem(
      REPORT_CHECKPOINT_KEY,
      JSON.stringify({
        sharedAppTableName: tableName,
        lastProcessedIndex,
        rows,
        totalPartners,
        updatedAt: new Date().toISOString(),
      })
    );
  } catch (err) {
    console.log("Could not save usability report checkpoint", err);
  }
};

const clearReportCheckpoint = () => {
  localStorage.removeItem(REPORT_CHECKPOINT_KEY);
};

const usabilityReportFilename = (tableName, suffix = "") =>
  `quicklink-usability-report-${tableName}${suffix}.xlsx`;

const credentialsHint = () => {
  if (isLocalhost()) {
    return "On localhost, credentials are kept in this browser tab until you close it (sessionStorage).";
  }
  return "Credentials are kept in memory only and cleared on refresh.";
};

const escapeCsv = (val) => {
  const s = val == null ? "" : String(val);
  if (/[,"\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
};

const downloadCsv = (filename, headers, rows) => {
  let csvContent = headers.map(escapeCsv).join(",") + "\n";
  rows.forEach((row) => {
    csvContent += headers.map((h) => escapeCsv(row[h])).join(",") + "\n";
  });
  const downloadAnchor = document.createElement("a");
  downloadAnchor.href =
    "data:text/csv;charset=utf-8," + encodeURIComponent(csvContent);
  downloadAnchor.download = filename;
  document.body.appendChild(downloadAnchor);
  downloadAnchor.click();
  document.body.removeChild(downloadAnchor);
};

const addReportSheet = (workbook, sheetName, headers, rows) => {
  const sheet = workbook.addWorksheet(sheetName);
  sheet.addRow(headers);
  sheet.getRow(1).font = { bold: true };

  rows.forEach((row) => {
    const dataRow = sheet.addRow(headers.map((header) => row[header] ?? ""));
    if (isReportRowIssue(row)) {
      dataRow.eachCell((cell) => {
        cell.fill = {
          type: "pattern",
          pattern: "solid",
          fgColor: { argb: "FFFEF2F2" },
        };
      });
    }
    const fixColIndex = headers.indexOf("Fix");
    if (fixColIndex >= 0) {
      const fixValue = String(row.Fix ?? "").trim();
      if (/^https?:\/\//i.test(fixValue)) {
        const cell = dataRow.getCell(fixColIndex + 1);
        cell.value = { text: fixValue, hyperlink: fixValue };
        cell.font = {
          ...(cell.font || {}),
          color: { argb: "FF0563C1" },
          underline: true,
        };
      }
    }
  });

  sheet.columns.forEach((column, index) => {
    let maxLength = headers[index]?.length ?? 10;
    column.eachCell({ includeEmpty: false }, (cell) => {
      const raw =
        cell.value && typeof cell.value === "object" && cell.value.text != null
          ? cell.value.text
          : cell.value;
      maxLength = Math.max(maxLength, String(raw ?? "").length);
    });
    const maxWidth = headers[index] === "Fix" ? 80 : 40;
    column.width = Math.min(maxLength + 2, maxWidth);
  });

  return sheet;
};

const triggerDownloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const downloadAnchor = document.createElement("a");
  downloadAnchor.href = url;
  downloadAnchor.download = filename;
  document.body.appendChild(downloadAnchor);
  downloadAnchor.click();
  document.body.removeChild(downloadAnchor);
  URL.revokeObjectURL(url);
};

const downloadUsabilityReportXlsx = async (filename, headers, rows) => {
  const workbook = new ExcelJS.Workbook();
  addReportSheet(workbook, "QuickLink usability", headers, rows);
  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownloadBlob(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
};

/** Final export: one workbook with all records split across 2 sheets (part 1 + part 2). */
const downloadCombinedUsabilityReportXlsx = async (filename, headers, rows) => {
  const workbook = new ExcelJS.Workbook();
  const midpoint = Math.ceil(rows.length / 2);
  const part1 = rows.slice(0, midpoint);
  const part2 = rows.slice(midpoint);

  addReportSheet(
    workbook,
    part2.length > 0 ? "All records (part 1)" : "All records",
    headers,
    part1
  );
  if (part2.length > 0) {
    addReportSheet(workbook, "All records (part 2)", headers, part2);
  }

  const buffer = await workbook.xlsx.writeBuffer();
  triggerDownloadBlob(
    new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    filename
  );
};

const configureAws = (accessKeyId, secretAccessKey) => {
      AWS.config.update({
    accessKeyId: accessKeyId.trim(),
    secretAccessKey: secretAccessKey.trim(),
    region: "us-west-2",
  });
};

const scanDynamoTable = async (tableName) => {
      const dynamodb = new AWS.DynamoDB();
      const items = [];
      let lastEvaluatedKey = null;

      do {
        const scanParams = {
          TableName: tableName,
          ...(lastEvaluatedKey && { ExclusiveStartKey: lastEvaluatedKey }),
        };
        const scanResult = await dynamodb.scan(scanParams).promise();
        const page = (scanResult.Items || []).map((item) =>
          AWS.DynamoDB.Converter.unmarshall(item)
        );
        items.push(...page);
        lastEvaluatedKey = scanResult.LastEvaluatedKey || null;
      } while (lastEvaluatedKey);

  return items;
};

const flattenForCsv = (obj, prefix = "") => {
        const row = {};
  if (obj == null || typeof obj !== "object") return row;
        Object.keys(obj).forEach((key) => {
    if (key === "__typename") return;
          const val = obj[key];
          const col = prefix ? `${prefix}.${key}` : key;
    if (
      val != null &&
      typeof val === "object" &&
      !Array.isArray(val) &&
      !(val instanceof Date)
    ) {
            Object.assign(row, flattenForCsv(val, col));
          } else {
            row[col] = Array.isArray(val) ? JSON.stringify(val) : val;
          }
        });
        return row;
      };

async function evaluatePartnerRow(app, requestContactByClientId = new Map()) {
  const partnerName = app.partnerName ?? "";
  const clientId = app.clientId ?? "";
  const useConnectedOrgs = !!app.useConnectedOrgs;
  const connectedOrg = useConnectedOrgs ? "ON" : "OFF";
  const email = app.submittingUser?.email?.trim() ?? "";
  const channel = (app.channel ?? "").trim();
  const requestContact = requestContactByClientId.get(String(clientId).trim()) || {};

  let token;
  if (
    useConnectedOrgs &&
    app.parentOrganizationClientId &&
    app.organizationId
  ) {
    const tokenResult = await getTokenWithRetry(
      app.parentOrganizationClientId,
      app.organizationId
    );
    token = tokenResult?.token;
  }

  let suExist = "";
  if (email && clientId) {
    try {
      const userRes = await getUserByEmail(clientId, email, token, true);
      suExist =
        userRes?.data?.length > 0 ? "true" : "false";
    } catch (err) {
      console.log("getUserByEmail failed for", clientId, email, err);
      suExist = "error";
    }
  }

  let hasOffers = "";
  if (channel && clientId) {
    try {
      const offersRes = useConnectedOrgs
        ? await getOffersForSharedApp({
            clientId,
            channel,
            useConnectedOrgs: true,
            parentOrganizationClientId: app.parentOrganizationClientId ?? "",
            organizationId: app.organizationId ?? "",
            token: token ?? null,
            throwOnError: true,
          })
        : await getOffersByChannel(clientId, channel, undefined, true);
      hasOffers =
        offersRes?.data?.length > 0 ? "Has Offers" : "No offers";
    } catch (err) {
      console.log("getOffers failed for", clientId, channel, err);
      hasOffers = "error";
    }
  }

  return {
    "Partner Name": partnerName,
    "Client ID": clientId,
    "Partner Primary Contact Email": requestContact.partnerEmail ?? "",
    "SF Account Owner Email": requestContact.accountOwnerEmail ?? "",
    Status: app.status ?? "",
    "Connected Org": connectedOrg,
    "Submitting User Email": email,
    "SU exist": suExist,
    "Channel Name": channel,
    "Has Offers": hasOffers,
    Fix: buildUsabilityReportFix({
      suExist,
      hasOffers,
      requestId: requestContact.requestId,
      sharedAppId: app.id,
    }),
  };
}

const ExportDynamoDBTable = ({ tableName, sharedAppTableName }) => {
  const [accessKeyId, setAccessKeyId] = useState(
    () => loadLocalhostCredentials()?.accessKeyId ?? ""
  );
  const [secretAccessKey, setSecretAccessKey] = useState(
    () => loadLocalhostCredentials()?.secretAccessKey ?? ""
  );
  const [credentialsOpen, setCredentialsOpen] = useState(false);
  const [draftAccessKeyId, setDraftAccessKeyId] = useState("");
  const [draftSecretAccessKey, setDraftSecretAccessKey] = useState("");
  const [reportRunning, setReportRunning] = useState(false);
  const [reportProgress, setReportProgress] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const [reportCheckpoint, setReportCheckpoint] = useState(() =>
    loadReportCheckpoint(sharedAppTableName)
  );
  const reportCancelRef = useRef(false);
  const tableScrollRef = useRef(null);
  const importFileRef = useRef(null);
  const [importRunning, setImportRunning] = useState(false);
  const [importProgress, setImportProgress] = useState("");
  const [importCsvText, setImportCsvText] = useState(null);
  const [importFileName, setImportFileName] = useState("");
  const [importFileRevision, setImportFileRevision] = useState(null);
  const [importPreview, setImportPreview] = useState(null);
  const [importFieldsToUpdate, setImportFieldsToUpdate] = useState(
    REQUEST_IMPORT_FORM_INITIAL.fieldsToUpdate
  );
  const [importResults, setImportResults] = useState(null);

  const requestsTableName = resolveRequestsTableName(tableName, sharedAppTableName);

  useEffect(() => {
    const checkpoint = loadReportCheckpoint(sharedAppTableName);
    setReportCheckpoint(checkpoint);
    setReportRows(checkpoint?.rows ?? []);
  }, [sharedAppTableName]);

  useEffect(() => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollTop = tableScrollRef.current.scrollHeight;
    }
  }, [reportRows]);

  useEffect(() => {
    if (!reportRunning) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [reportRunning]);

  const hasCredentials =
    accessKeyId.trim() !== "" && secretAccessKey.trim() !== "";

  const openCredentialsModal = () => {
    setDraftAccessKeyId(accessKeyId);
    setDraftSecretAccessKey(secretAccessKey);
    setCredentialsOpen(true);
  };

  const dismissCredentials = () => {
    setCredentialsOpen(false);
    setDraftAccessKeyId("");
    setDraftSecretAccessKey("");
  };

  const saveCredentials = () => {
    const nextAccessKeyId = draftAccessKeyId.trim();
    const nextSecretAccessKey = draftSecretAccessKey.trim();
    setAccessKeyId(nextAccessKeyId);
    setSecretAccessKey(nextSecretAccessKey);
    saveLocalhostCredentials(nextAccessKeyId, nextSecretAccessKey);
    dismissCredentials();
  };

  const clearCredentials = () => {
    setAccessKeyId("");
    setSecretAccessKey("");
    clearLocalhostCredentials();
  };

  const canSaveCredentials =
    draftAccessKeyId.trim() !== "" && draftSecretAccessKey.trim() !== "";

  const handleExport = async () => {
    if (!hasCredentials) return;

    try {
      configureAws(accessKeyId, secretAccessKey);
      const items = await scanDynamoTable(tableName);

      const flatItems = items.map((item) => flattenForCsv(item));

      const allKeys = new Set();
      flatItems.forEach((row) =>
        Object.keys(row).forEach((k) => allKeys.add(k))
      );
      let headers = [...allKeys].sort();

      if (tableName && tableName.startsWith("Requests-")) {
        headers = headers.filter((h) => h !== "notes" && h !== "requestNotes");
      }

      const rows = flatItems.map((row) => {
        const out = {};
        headers.forEach((h) => {
          out[h] = row[h];
        });
        return out;
      });

      downloadCsv(`${tableName}.csv`, headers, rows);
    } catch (error) {
      console.error("Error exporting DynamoDB table:", error);
    }
  };

  const handleCancelReport = () => {
    reportCancelRef.current = true;
    setReportProgress("Cancelling…");
  };

  const handleClearReportCheckpoint = () => {
    clearReportCheckpoint();
    setReportCheckpoint(null);
    setReportRows([]);
    setReportProgress("");
  };

  const runUsabilityReport = async ({ resume = false } = {}) => {
    if (!hasCredentials || !sharedAppTableName || reportRunning) return;

    reportCancelRef.current = false;
    setReportRunning(true);

    let rows = [];
    let startIndex = 0;

    try {
      const checkpoint = resume ? loadReportCheckpoint(sharedAppTableName) : null;
      if (resume) {
        if (!checkpoint) {
          setReportProgress("No saved checkpoint found for this table.");
          return;
        }
        rows = [...checkpoint.rows];
        startIndex = checkpoint.lastProcessedIndex;
        setReportRows(rows);
        setReportProgress(
          `Resuming from partner ${startIndex + 1} (${rows.length} already processed)...`
        );
      } else {
        clearReportCheckpoint();
        setReportCheckpoint(null);
        setReportRows([]);
      }

      configureAws(accessKeyId, secretAccessKey);
      setReportProgress("Scanning partners table...");
      const partners = (await scanDynamoTable(sharedAppTableName)).filter(
        (app) => app.status === "ACTIVE"
      );

      if (reportCancelRef.current) {
        setReportProgress("Cancelled before processing partners.");
        return;
      }

      setReportProgress("Scanning requests for contact emails...");
      const requestContactByClientId = buildRequestContactByClientId(
        await scanDynamoTable(requestsTableName)
      );

      if (reportCancelRef.current) {
        setReportProgress("Cancelled before processing partners.");
        return;
      }

      for (let i = startIndex; i < partners.length; i += 1) {
        if (reportCancelRef.current) {
          setReportProgress(
            `Cancelled — ${rows.length} of ${partners.length} partners checked.`
          );
          break;
        }

        const app = partners[i];
        setReportProgress(
          `Checking partner ${i + 1} of ${partners.length}: ${app.clientId ?? app.id ?? ""}`
        );
        const row = await evaluatePartnerRow(app, requestContactByClientId);
        rows.push(row);
        setReportRows([...rows]);

        if (rows.length % CHECKPOINT_INTERVAL === 0) {
          await downloadUsabilityReportXlsx(
            usabilityReportFilename(
              sharedAppTableName,
              `-checkpoint-${rows.length}`
            ),
            REPORT_HEADERS,
            rows
          );
          saveReportCheckpoint(
            sharedAppTableName,
            rows.length,
            rows,
            partners.length
          );
          setReportCheckpoint(loadReportCheckpoint(sharedAppTableName));
          setReportProgress(
            `Checkpoint saved — ${rows.length} of ${partners.length} partners processed.`
          );
        }
      }

      if (!reportCancelRef.current) {
        await downloadCombinedUsabilityReportXlsx(
          usabilityReportFilename(sharedAppTableName, "-complete"),
          REPORT_HEADERS,
          rows
        );
        clearReportCheckpoint();
        setReportCheckpoint(null);
        setReportProgress(formatReportCompleteMessage(rows));
      }
    } catch (error) {
      console.error("Error generating submitting user report:", error);
      setReportProgress("Report failed — see console for details.");
    } finally {
      setReportRunning(false);
      reportCancelRef.current = false;
    }
  };

  const handleSubmittingUserReport = () => runUsabilityReport({ resume: false });
  const handleResumeUsabilityReport = () => runUsabilityReport({ resume: true });

  const runRequestEmailImport = async (text, fieldsToUpdate) => {
    if (!text || importRunning) return;

    const selectedImportFields = selectedImportFieldsFromForm(fieldsToUpdate);
    const inspection = inspectRequestImportCsv(text, fieldsToUpdate);

    if (!inspection.canProcess) {
      setImportProgress("Fix CSV issues before processing.");
      setImportPreview(inspection);
      return;
    }

    const { rows } = parseCsvText(text);

    setImportRunning(true);
    setImportProgress(`Processing 0 of ${rows.length}...`);
    setImportResults(buildInitialImportResults(rows, selectedImportFields));

    const patchImportResult = (index, patch) => {
      setImportResults((prev) => {
        if (!prev) return prev;
        const next = [...prev];
        next[index] = { ...next[index], ...patch };
        return next;
      });
    };

    try {
      let updated = 0;
      let skipped = 0;
      let failed = 0;

      for (let i = 0; i < rows.length; i += 1) {
        const row = rows[i];
        const recordId = row.id?.trim();
        if (!recordId) {
          skipped += 1;
          patchImportResult(i, {
            status: IMPORT_RESULT_STATUS.SKIPPED,
            message: "Missing id",
          });
          continue;
        }

        const updates = buildImportRowUpdates(row, selectedImportFields);

        if (Object.keys(updates).length === 0) {
          skipped += 1;
          patchImportResult(i, {
            status: IMPORT_RESULT_STATUS.SKIPPED,
            message: "All selected email fields empty",
          });
          continue;
        }

        setImportProgress(
          `Processing ${i + 1} of ${rows.length} — record ${recordId}`
        );

        try {
          await updateRequestFieldsViaGraphql(recordId, updates);
          updated += 1;
          const fieldCount = Object.keys(updates).length;
          patchImportResult(i, {
            status: IMPORT_RESULT_STATUS.UPDATED,
            message:
              fieldCount === selectedImportFields.length
                ? "Updated successfully"
                : `Updated ${fieldCount} field(s); empty fields left unchanged`,
            updates,
          });
        } catch (err) {
          failed += 1;
          console.error("Request import failed for", recordId, err);
          patchImportResult(i, {
            status: IMPORT_RESULT_STATUS.FAILED,
            message: formatImportError(err),
            updates,
          });
        }
      }

      setImportProgress(
        `Import complete — ${updated} updated, ${skipped} skipped, ${failed} failed.`
      );
    } catch (err) {
      console.error("Request email import failed:", err);
      setImportProgress("Import failed — see console for details.");
    } finally {
      setImportRunning(false);
    }
  };

  const handleImportPreviewChange = useCallback((preview, fieldsToUpdate) => {
    setImportPreview(preview);
    if (fieldsToUpdate) {
      setImportFieldsToUpdate(fieldsToUpdate);
    }
    if (!preview) return;
    setImportProgress((prev) =>
      prev.startsWith("Processing") || prev.startsWith("Import complete")
        ? prev
        : preview.canProcess
        ? `Inspection complete — ${preview.rowCount} row${preview.rowCount === 1 ? "" : "s"} ready to import.`
        : "Inspection found issues — fix the CSV or field selection before processing."
    );
  }, []);

  const handleChooseImportFile = () => {
    if (importFileRef.current) {
      importFileRef.current.value = "";
    }
    importFileRef.current?.click();
  };

  const handleImportFileSelected = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportCsvText(null);
    setImportFileName("");
    setImportFileRevision(null);
    setImportPreview(null);
    setImportResults(null);
    setImportProgress("Inspecting CSV...");

    try {
      const text = await file.text();
      setImportCsvText(text);
      setImportFileName(file.name);
      setImportFileRevision(`${file.name}-${file.lastModified}-${file.size}`);
    } catch (err) {
      console.error("CSV inspection failed:", err);
      setImportCsvText(null);
      setImportFileName("");
      setImportFileRevision(null);
      setImportPreview(null);
      setImportResults(null);
      setImportProgress("Could not read CSV file.");
    } finally {
      event.target.value = "";
    }
  };

  const handleProcessImport = async () => {
    if (!importCsvText || !importPreview?.canProcess) return;
    await runRequestEmailImport(importCsvText, importFieldsToUpdate);
  };

  const handleClearImportPreview = () => {
    if (importRunning) return;
    if (importFileRef.current) {
      importFileRef.current.value = "";
    }
    setImportCsvText(null);
    setImportFileName("");
    setImportFileRevision(null);
    setImportPreview(null);
    setImportResults(null);
    setImportProgress("");
  };

  const hasImportPreview = Boolean(
    importCsvText || importPreview || importResults?.length
  );

  return (
    <div>
      <Box
        sx={{
          display: "flex",
          gap: 3,
          alignItems: "center",
          flexWrap: "wrap",
          padding: "10px",
        }}
      >
        <LumosButton onClick={openCredentialsModal}>
          {hasCredentials ? "Update AWS credentials" : "Set AWS credentials"}
        </LumosButton>
        <LumosButton onClick={handleExport} disabled={!hasCredentials}>
          Export CSV
        </LumosButton>
        {hasCredentials && (
          <Text sx={{ fontSize: 1, color: "muted" }}>
            {credentialsHint()}
          </Text>
        )}
        {hasCredentials && isLocalhost() && (
          <LumosButton onClick={clearCredentials}>
            Clear credentials
          </LumosButton>
        )}
      </Box>

      {hasCredentials && sharedAppTableName && (
        <Box
          sx={{
            mt: 4,
            pt: 3,
            padding: "10px",
            borderTop: "1px solid",
            borderColor: "neutral.l3",
          }}
        >
          <Box
            sx={{
              mb: 3,
              px: 3,
              py: 2,
              fontSize: 1,
              fontWeight: 600,
              color: "#b45309",
              backgroundColor: "#fffbeb",
              border: "1px solid #fcd34d",
              borderRadius: 4,
            }}
          >
            Warning: This will loop through all accounts and may take a while to
            complete. Please do not refresh or close the tab until the report is
            complete.
          </Box>
          <Box sx={{ display: "flex", gap: 3, alignItems: "center", flexWrap: "wrap" }}>
            <LumosButton
              onClick={handleSubmittingUserReport}
              disabled={reportRunning}
            >
              {reportRunning ? (
                <Box
                  as="span"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 2,
                  }}
                >
                  <Spinner size={20} />
                  Generating QuickLink usability report...
                </Box>
              ) : (
                "Generate QuickLink usability report"
              )}
            </LumosButton>            
            {reportCheckpoint && !reportRunning && (
              <>
                <LumosButton onClick={handleResumeUsabilityReport}>
                  Resume from partner {reportCheckpoint.lastProcessedIndex + 1}
                </LumosButton>
                <LumosButton onClick={handleClearReportCheckpoint}>
                  Clear checkpoint
                </LumosButton>
              </>
            )}
            {reportRunning && (
              <LumosButton onClick={handleCancelReport}>
                Cancel
              </LumosButton>
            )}
          </Box>

          {reportCheckpoint && !reportRunning && (
            <Text sx={{ mt: 2, px: 3, fontSize: 1, color: "muted" }}>
              Saved checkpoint: {reportCheckpoint.lastProcessedIndex} partners
              processed
              {reportCheckpoint.totalPartners
                ? ` of ${reportCheckpoint.totalPartners}`
                : ""}
              .
            </Text>
          )}
          {reportRunning && (
            <Text
              sx={{
                mt: 3,
                px: 3,
                py: 2,
                fontSize: 1,
                fontWeight: 600,
                color: "#b45309",
                backgroundColor: "#fffbeb",
                borderRadius: 4,
                display: "block",
              }}
            >
              Report in progress — do not refresh or close this tab. Checkpoints
              save every 500 partners.
            </Text>
          )}
          {reportProgress && (
            <Text
              sx={{
                mt: 3,
                px: 3,
                pb: 2,
                fontSize: 1,
                color: "muted",
                display: "block",
              }}
            >
              {renderReportProgress(reportProgress)}
            </Text>
          )}
          {(reportRunning || reportRows.length > 0) && (
            <Box
              ref={tableScrollRef}
              sx={{
                mt: 3,
                maxHeight: 420,
                overflowY: "auto",
                overflowX: "hidden",
                border: "1px solid",
                borderColor: "neutral.l3",
                borderRadius: 4,
                backgroundColor: "#ffffff",
              }}
            >
              <Box
                as="table"
                sx={{
                  width: "100%",
                  tableLayout: "fixed",
                  borderCollapse: "separate",
                  borderSpacing: 0,
                  fontSize: 1,
                  "th, td": {
                    borderBottom: "1px solid",
                    borderColor: "neutral.l3",
                    px: 2,
                    py: 2,
                    textAlign: "left",
                    verticalAlign: "top",
                    whiteSpace: "normal",
                    wordBreak: "break-word",
                    overflowWrap: "anywhere",
                  },
                  thead: {
                    position: "sticky",
                    top: 0,
                    zIndex: 2,
                  },
                  th: {
                    position: "sticky",
                    top: 0,
                    backgroundColor: "#ffffff",
                    fontWeight: 600,
                    zIndex: 2,
                    boxShadow: "0 1px 0 #e0e0e0",
                  },
                }}
              >
                <Box as="thead">
                  <Box as="tr">
                    {REPORT_HEADERS.map((header) => (
                      <Box as="th" key={header}>
                        {header}
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box as="tbody">
                  {reportRows.map((row, index) => (
                    <Box
                      as="tr"
                      key={`${row["Client ID"]}-${index}`}
                      sx={{
                        backgroundColor: isReportRowIssue(row)
                          ? REPORT_ROW_ISSUE_BG
                          : undefined,
                      }}
                    >
                      {REPORT_HEADERS.map((header) => (
                        <Box as="td" key={header}>
                          {row[header] ?? ""}
                        </Box>
                      ))}
                    </Box>
                  ))}
                </Box>
              </Box>
            </Box>
          )}

          <Box
            sx={{
              mt: 4,
              pt: 3,
              borderTop: "1px solid",
              borderColor: "neutral.l3",
            }}
          >
            <Text sx={{ fontSize: 2, fontWeight: 600, mb: 2, display: "block" }}>
              Import request contact emails
            </Text>
            <Text sx={{ fontSize: 1, color: "muted", mb: 2, display: "block", lineHeight: 1.2 }}>
              Upload a CSV to update email fields on the Requests table (
              <Box as="span" sx={{ fontFamily: "monospace", fontSize: 0 }}>
                {requestsTableName}
              </Box>
              ). Updates use your signed-in AppSync session (not AWS export credentials).
              <Box as="span" sx={{ display: "block", mt: 2 }}>
                Only the fields you check below will be updated; unchecked fields
                are left unchanged.
              </Box>
            </Text>

            <Box
              sx={{
                mb: 3,
                mt: 2,
                p: 3,
                border: "1px solid",
                borderColor: "neutral.l3",
                borderRadius: 4,
                backgroundColor: "#f9fafb",
                overflowX: "auto",
              }}
            >
              <Text sx={{ fontSize: 1, fontWeight: 600, mb: 2 }}>
                Sample CSV format
              </Text>
              <Box
                as="pre"
                sx={{
                  m: 0,
                  fontSize: 0,
                  fontFamily: "monospace",
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {REQUEST_IMPORT_SAMPLE_CSV}
              </Box>
              <Box sx={{ mt: 2 }}>
                <LumosButton onClick={downloadSampleImportCsv}>
                  Download sample CSV
                </LumosButton>
              </Box>
            </Box>

            <LumosForm
              initialValues={REQUEST_IMPORT_FORM_INITIAL}
              onSubmit={() => {}}
              showSummary={false}
            >
              <RequestImportPreviewWatcher
                csvText={importCsvText}
                fileRevision={importFileRevision}
                onPreviewChange={handleImportPreviewChange}
              />
              <ChoiceInput
                name="fieldsToUpdate"
                allowMultiple
                label="Fields to update"
                description="Only checked fields are written to the Requests table; unchecked fields are left unchanged."
                items={REQUEST_IMPORT_FIELDS.map((field) => ({
                  label: field.label,
                  value: field.dynamoField,
                }))}
              />
              <RequestImportUploadActions
                importRunning={importRunning}
                importFileRef={importFileRef}
                onChooseFile={handleChooseImportFile}
                onFileSelected={handleImportFileSelected}
                onClear={handleClearImportPreview}
                hasImportPreview={hasImportPreview}
              />
              <RequestImportPreview
                fileName={importFileName}
                preview={importPreview}
              />
              <RequestImportProcessButton
                importRunning={importRunning}
                onProcess={handleProcessImport}
                canProcess={Boolean(importPreview?.canProcess)}
              />
            </LumosForm>
            {importProgress && (
              <Text
                sx={{
                  mt: 2,
                  fontSize: 1,
                  color: importRunning ? "#b45309" : "muted",
                  fontWeight: importRunning ? 600 : 400,
                }}
              >
                {importProgress}
              </Text>
            )}
            <RequestImportResultsTable
              results={importResults}
              selectedImportFields={importPreview?.selectedImportFields}
              importRunning={importRunning}
            />
          </Box>


        </Box>
      )}

      <Modal
        isOpen={credentialsOpen}
        onDismiss={dismissCredentials}
        title="AWS credentials"
      >
        <Text sx={{ mb: 3, fontSize: 1 }}>
          Enter temporary credentials for DynamoDB export.{" "}
          {isLocalhost()
            ? "On localhost they are stored in sessionStorage for this tab only and are removed when you close the browser tab."
            : "They are kept in memory only and are not written to disk or local storage."}
        </Text>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mb: 2 }}>
          <label>
            <Text sx={{ display: "block", mb: 1, fontWeight: 600 }}>
              Access key ID
            </Text>
            <input
              type="text"
              autoComplete="off"
              value={draftAccessKeyId}
              onChange={(e) => setDraftAccessKeyId(e.target.value)}
              style={{ width: "100%", padding: "8px", boxSizing: "border-box" }}
            />
          </label>
          <label>
            <Text sx={{ display: "block", mb: 1, fontWeight: 600 }}>
              Secret access key
            </Text>
            <input
              type="password"
              autoComplete="off"
              value={draftSecretAccessKey}
              onChange={(e) => setDraftSecretAccessKey(e.target.value)}
              style={{ width: "100%", padding: "8px", boxSizing: "border-box" }}
            />
          </label>
        </Box>
        <ModalControls
          actions={[
            {
              onClick: saveCredentials,
              label: "Use credentials",
              disabled: !canSaveCredentials,
            },
            {
              onClick: dismissCredentials,
              label: "Cancel",
            },
          ]}
        />
      </Modal>
    </div>
  );
};

export default ExportDynamoDBTable;
